/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { cn } from '@/shared/ui/utils/tailwind';

interface PageTitleProps {
  children: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
  'data-testid'?: string;
}

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 'var(--text-app-title)',
  fontWeight: 'var(--font-bold)' as unknown as number,
  letterSpacing: 'var(--tracking-app-title)',
  color: 'var(--text-primary)',
};

/**
 * Canonical page-title heading for authenticated pages.
 *
 * Renders an h1 styled with DS tokens (size, weight, tracking, color).
 * All authenticated agency page titles MUST use this component.
 */
export function PageTitle({ children, icon, className, ...props }: PageTitleProps) {
  if (icon) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span data-testid="page-header-icon" style={{ display: 'flex', color: 'var(--seed-base)' }}>{icon}</span>
        <h1 className={cn(className)} style={titleStyle} {...props}>
          {children}
        </h1>
      </div>
    );
  }

  return (
    <h1 className={cn(className)} style={titleStyle} {...props}>
      {children}
    </h1>
  );
}
